import React from 'react';
import { Container, Row, Col, Card, CardBody, Badge } from 'reactstrap';
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

import CharacterAvatar from "../../../Components/Common/CharacterAvatar";
import WelcomePointsModal from "../../../Components/Common/WelcomePointsModal";

import { getBattle, getNextBattle } from "../../../helpers/api/battle";
import {
  BattleBettingVolumeSnapshot,
  getBattleBettingVolume,
} from "../../../helpers/api/bettingVolume";
import {
  BettingPoolSocket,
  createBettingPoolSocket,
} from "../../../helpers/socket/bettingPool";
import { withStableBattleParticipations } from "../../../helpers/battle/participations";
import BetCard from "../Bet/BetCard";
import Arena from "../../../Components/Common/Arena";
import type { BattleBet, BattleDetail, BattleParticipation, BattlePool } from "../../../types/api.generated";

export type BetLike = BattleBet;

export type BattleLike = Omit<BattleDetail, "participations" | "bets"> & {
  participations: ParticipationUi[];
  bets?: BetLike[];
};

export type ParticipationLike = BattleParticipation;

export type PoolLike = BattlePool;

export interface ParticipationUi extends ParticipationLike {
  id: string;
  character: {
    id?: string;
    name: string;
    avatar: string | null;
  };
  pool: PoolLike & {
    totalVolume: number;
  };
}

export type BetActionType = "created" | "cancelled" | "increased" | "switched";

export interface BetActionPayload {
  type: BetActionType;
  participationId?: string;
  amount?: number;
  message?: string;
}

type CountdownState = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  hasStarted: boolean;
  hasEnded: boolean;
  isInProgress: boolean;
};

const getCountdownState = (startTime?: string, durationSec?: number): CountdownState => {
  const targetTime = startTime ? new Date(startTime).getTime() : NaN;
  const remainingMs = Number.isNaN(targetTime) ? 0 : Math.max(targetTime - Date.now(), 0);
  const durationMs = Number(durationSec || 0) * 1000;
  const endTime = Number.isNaN(targetTime) ? NaN : targetTime + durationMs;
  const hasStarted = Number.isFinite(targetTime) && Date.now() >= targetTime;
  const hasEnded = Number.isFinite(endTime) && durationMs > 0 && Date.now() >= endTime;

  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
    hasStarted,
    hasEnded,
    isInProgress: hasStarted && !hasEnded,
  };
};

const formatCountdownValue = (value: number) => String(value).padStart(2, "0");

interface DashboardProps {
  battleId?: string;
}

const Dashboard: React.FC<DashboardProps> = ({ battleId }) => {
  document.title = 'Legends | The Colosseum — Dashboard';

  const [battle, setBattle] = React.useState<BattleLike | null>(null);
  const [bets, setBets] = React.useState<BetLike[]>([]);
  const [countdown, setCountdown] = React.useState<CountdownState>(() => getCountdownState());
  const [showWelcomeModal, setShowWelcomeModal] = React.useState(
    () => sessionStorage.getItem("showWelcomePointsModal") === "true"
  );
  const bettingPoolSocketRef = React.useRef<BettingPoolSocket | null>(null);

  React.useEffect(() => {
    if (showWelcomeModal) {
      sessionStorage.removeItem("showWelcomePointsModal");
    }
  }, [showWelcomeModal]);

  const handleBetAction = React.useCallback((payload: BetActionPayload) => {
    if (!payload.message) return;

    toast(payload.message, {
      position: "top-right",
      hideProgressBar: true,
      className: 'bg-success text-white',
    });
  }, []);

  const loadNextBattle = React.useCallback(async () => {
    try {
      const res = battleId ? await getBattle(battleId) : await getNextBattle();
      const nextBattle = withStableBattleParticipations(res as BattleLike);

      setBattle(nextBattle);
      setBets(nextBattle.bets ?? []);
    } catch (error) {
      console.error('Error fetching battle data:', error);
    }
  }, [battleId]);

  React.useEffect(() => {
    if (!battle || (battleId && String(battle.id) !== String(battleId))) {
      loadNextBattle();
    }
  }, [battle, battleId, loadNextBattle]);

  const applyBattleVolumeSnapshot = React.useCallback((snapshot: BattleBettingVolumeSnapshot) => {
    setBattle((prev) => {
      if (!prev || prev.id !== snapshot.battleId) return prev;

      const poolsByParticipationId = new Map(
        snapshot.pools.map((pool) => [pool.participationId, pool])
      );

      return {
        ...prev,
        participations: prev.participations.map((participation) => {
          const updatedPool = poolsByParticipationId.get(participation.id);
          if (!updatedPool) return participation;

          return {
            ...participation,
            pool: {
              ...participation.pool,
              totalVolume: updatedPool.totalVolume,
              betsCount: updatedPool.betsCount,
            },
          };
        }),
      };
    });
  }, []);

  const refreshBattleVolume = React.useCallback(async () => {
    const battleId = battle?.id;
    if (!battleId) return;

    try {
      const snapshot = await getBattleBettingVolume(battleId);
      applyBattleVolumeSnapshot(snapshot);
    } catch (error) {
      console.error("Error refreshing battle betting volume:", error);
    }
  }, [battle?.id, applyBattleVolumeSnapshot]);

  React.useEffect(() => {
    const battleId = battle?.id;
    const token = sessionStorage.getItem("tokenUser");

    if (!battleId || !token) return;

    let active = true;
    const socket = createBettingPoolSocket(token);
    bettingPoolSocketRef.current = socket;

    const syncBattleVolume = async () => {
      try {
        const snapshot = await getBattleBettingVolume(battleId);
        if (active) {
          applyBattleVolumeSnapshot(snapshot);
        }
      } catch (error) {
        console.error("Error fetching battle betting volume:", error);
      }
    };

    const subscribeToBattle = () => {
      socket.emit("battle:subscribe", { battleId });
      syncBattleVolume();
    };

    socket.on("connect", subscribeToBattle);

    socket.on("battle:volume.updated", (snapshot) => {
      if (snapshot.battleId === battleId) {
        applyBattleVolumeSnapshot(snapshot);
      }
    });

    socket.on("battle:error", (payload) => {
      console.error("Battle betting socket error:", payload.message);
    });

    socket.connect();
    syncBattleVolume();

    return () => {
      active = false;
      socket.off("connect", subscribeToBattle);
      socket.off("battle:volume.updated");
      socket.off("battle:error");
      if (socket.connected) {
        socket.emit("battle:unsubscribe", { battleId });
      }
      socket.disconnect();
      if (bettingPoolSocketRef.current === socket) {
        bettingPoolSocketRef.current = null;
      }
    };
  }, [battle?.id, applyBattleVolumeSnapshot]);

  const upsertBet = React.useCallback((updatedBet: BetLike) => {
    setBets((prev) => {
      const index = prev.findIndex((b) => b.id === updatedBet.id);

      if (index === -1) {
        return [...prev, updatedBet];
      }

      const next = [...prev];
      next[index] = updatedBet;
      return next;
    });
  }, []);

  const removeBet = React.useCallback((betId: string) => {
    setBets((prev) => prev.filter((bet) => bet.id !== betId));
  }, []);

  const orderedParticipations = React.useMemo(() => {
    return battle ? withStableBattleParticipations(battle).participations : [];
  }, [battle]);

  React.useEffect(() => {
    setCountdown(getCountdownState(battle?.startTime, battle?.durationSec));

    if (!battle?.startTime) return;

    const countdownInterval = window.setInterval(() => {
      setCountdown(getCountdownState(battle.startTime, battle.durationSec));
    }, 1000);

    return () => window.clearInterval(countdownInterval);
  }, [battle?.durationSec, battle?.startTime]);

  if (!battle || orderedParticipations.length < 2) {
    return null;
  }

  const participation1 = orderedParticipations[0];
  const participation2 = orderedParticipations[1];
  const isBattleActive = String(battle.status ?? "").toLowerCase() === "active";

  return (
    <React.Fragment>
      <div className="page-content" data-cy="dashboard-page">
        <Container fluid>
          <Row className="g-4 align-items-center mb-4 animate-fade-in">
            <Col xs={12} md={3} className="text-center">
              <div className="fighter-portrait-card d-inline-flex flex-column align-items-center gap-2">
                <CharacterAvatar
                  avatar={participation1.character.avatar}
                  fallbackClassName="fighter-portrait fighter-portrait--fallback rounded-circle"
                  imageClassName="fighter-portrait img-fluid rounded-circle"
                  imageStyle={{ maxWidth: 140 }}
                  name={participation1.character.name}
                />
                <div className="fw-bold text-uppercase text-primary neon-text-cyan">{participation1.character.name}</div>
              </div>
            </Col>

            <Col xs={12} md={6} className="text-center">
              <Card className="battle-countdown-card cyber-glass-elevated border-0 overflow-hidden animate-neon-pulse">
                <CardBody className="p-0 position-relative">
                  <div className="battle-countdown-status position-absolute top-0 start-0 p-3">
                    <Badge color={countdown.hasEnded ? "secondary" : countdown.hasStarted ? "success" : "danger"} pill>
                      {countdown.hasEnded ? "Battle Ended" : countdown.hasStarted ? "Battle Started" : "Upcoming Battle"}
                    </Badge>
                  </div>
                  <div className="battle-countdown-frame ratio ratio-21x9">
                    {countdown.hasStarted ? (
                      <Arena selectedBattle={battle} variant="slot" />
                    ) : (
                      <div
                        className="battle-countdown-stage dash-countdown d-flex flex-column align-items-center justify-content-center"
                        data-cy="battle-countdown-stage"
                      >
                        <div className="battle-countdown-kicker text-uppercase text-muted fw-semibold">
                          Battle starts in
                        </div>
                        <div className="battle-countdown-list countdownlist justify-content-center w-100">
                          {[
                            { label: "Days", value: countdown.days },
                            { label: "Hours", value: countdown.hours },
                            { label: "Minutes", value: countdown.minutes },
                            { label: "Seconds", value: countdown.seconds },
                          ].map((item) => (
                            <div className="battle-countdown-unit countdownlist-item" data-cy="battle-countdown-unit" key={item.label}>
                              <div className="battle-countdown-label count-title">{item.label}</div>
                              <div className="battle-countdown-value count-num">{formatCountdownValue(item.value)}</div>
                            </div>
                          ))}
                        </div>
                        <div className="battle-countdown-versus fw-bold">VS</div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xs={12} md={3} className="text-center">
              <div className="fighter-portrait-card d-inline-flex flex-column align-items-center gap-2">
                <CharacterAvatar
                  avatar={participation2.character.avatar}
                  fallbackClassName="fighter-portrait fighter-portrait--fallback rounded-circle"
                  imageClassName="fighter-portrait img-fluid rounded-circle"
                  imageStyle={{ maxWidth: 140 }}
                  name={participation2.character.name}
                />
                <div className="fw-bold text-uppercase text-danger neon-text-purple">{participation2.character.name}</div>
              </div>
            </Col>
          </Row>

          <Row className="g-4 animate-fade-in">
            <Col xs={12} md={6}>
              <BetCard
                participation={participation1}
                oppositeParticipation={participation2}
                bets={bets}
                extraClass="text-primary"
                onBetAction={handleBetAction}
                onRefreshBattleVolume={refreshBattleVolume}
                onUpsertBet={upsertBet}
                onRemoveBet={removeBet}
                onReplaceUserBetForBattle={upsertBet}
                actionsDisabled={!isBattleActive}
              />
            </Col>

            <Col xs={12} md={6}>
              <BetCard
                participation={participation2}
                oppositeParticipation={participation1}
                bets={bets}
                extraClass="text-danger"
                onBetAction={handleBetAction}
                onRefreshBattleVolume={refreshBattleVolume}
                onUpsertBet={upsertBet}
                onRemoveBet={removeBet}
                onReplaceUserBetForBattle={upsertBet}
                actionsDisabled={!isBattleActive}
              />
            </Col>
          </Row>
        </Container>
      </div>

      <ToastContainer />
      {showWelcomeModal && (
        <WelcomePointsModal action="button" onDismiss={() => setShowWelcomeModal(false)} />
      )}
    </React.Fragment>
  );
};

export default Dashboard;
